import { supabase } from "../lib/supabase";

import {
  NOVA_MENU_CATEGORIES,
  NOVA_MENU_ITEMS,
  type NovaMenuCategory,
  type NovaMenuItem,
  type NovaMenuVariant,
} from "./nova-menu";

export type NovaMenuCatalogSource =
  | "supabase"
  | "fallback";

export type NovaMenuCatalog = {
  categories: readonly NovaMenuCategory[];
  items: readonly NovaMenuItem[];
  source: NovaMenuCatalogSource;
};

type MenuCategoryRow = {
  id: string;
  name: string;
  group_name: "food" | "drinks";
  sort_order: number;
};

type MenuItemRow = {
  id: string;
  category_id: string;
  name: string;
  description: string | null;
  base_price: number | null;
  image_url: string | null;
  sort_order: number;
  is_available: boolean;
  is_featured: boolean;
  ordering_enabled: boolean;
};

type MenuVariantRow = {
  id: string;
  item_id: string;
  label: string;
  price: number;
  sort_order: number;
};

function fallbackCatalog(): NovaMenuCatalog {
  return {
    categories:
      NOVA_MENU_CATEGORIES,
    items:
      NOVA_MENU_ITEMS,
    source:
      "fallback",
  };
}

function normalizeCategory(
  row: MenuCategoryRow,
): NovaMenuCategory {
  return {
    id: row.id,
    label: row.name,
    group: row.group_name,
    sortOrder:
      row.sort_order,
  };
}

function normalizeVariant(
  row: MenuVariantRow,
): NovaMenuVariant {
  return {
    id: row.id,
    label: row.label,
    price: row.price,
  };
}

async function loadNovaMenuCatalog(
  visibilityColumn:
    | "website_visible"
    | "qr_visible",
): Promise<NovaMenuCatalog> {
  try {
    const [
      categoriesResult,
      itemsResult,
      variantsResult,
    ] = await Promise.all([
      supabase
        .from("menu_categories")
        .select(
          "id,name,group_name,sort_order",
        )
        .eq(
          "is_published",
          true,
        )
        .order(
          "sort_order",
          {
            ascending: true,
          },
        ),

      supabase
        .from("menu_items")
        .select(
          [
            "id",
            "category_id",
            "name",
            "description",
            "base_price",
            "image_url",
            "sort_order",
            "is_available",
            "is_featured",
            "ordering_enabled",
          ].join(","),
        )
        .eq(
          "is_published",
          true,
        )
        .eq(
          visibilityColumn,
          true,
        )
        .order(
          "sort_order",
          {
            ascending: true,
          },
        ),

      supabase
        .from(
          "menu_item_variants",
        )
        .select(
          [
            "id",
            "item_id",
            "label",
            "price",
            "sort_order",
          ].join(","),
        )
        .eq(
          "is_published",
          true,
        )
        .eq(
          "is_available",
          true,
        )
        .order(
          "sort_order",
          {
            ascending: true,
          },
        ),
    ]);

    if (
      categoriesResult.error
    ) {
      throw categoriesResult.error;
    }

    if (
      itemsResult.error
    ) {
      throw itemsResult.error;
    }

    if (
      variantsResult.error
    ) {
      throw variantsResult.error;
    }

    const categoryRows =
      (categoriesResult.data ??
        []) as MenuCategoryRow[];

    const itemRows =
      (itemsResult.data ??
        []) as unknown as MenuItemRow[];

    const variantRows =
      (variantsResult.data ??
        []) as unknown as MenuVariantRow[];

    const variantsByItem =
      new Map<
        string,
        NovaMenuVariant[]
      >();

    for (
      const row of variantRows
    ) {
      const existing =
        variantsByItem.get(
          row.item_id,
        ) ?? [];

      existing.push(
        normalizeVariant(row),
      );

      variantsByItem.set(
        row.item_id,
        existing,
      );
    }

    const categories =
      categoryRows.map(
        normalizeCategory,
      );

    const items: NovaMenuItem[] =
      itemRows.map((row) => {
        const variants =
          variantsByItem.get(
            row.id,
          );

        return {
          id: row.id,
          categoryId:
            row.category_id,
          name: row.name,
          description:
            row.description ??
            undefined,
          price:
            row.base_price ??
            undefined,
          variants:
            variants?.length
              ? variants
              : undefined,
          image:
            row.image_url ??
            undefined,
          available:
            row.is_available,
          featured:
            row.is_featured,
          orderingEnabled:
            row.ordering_enabled,
          sortOrder:
            row.sort_order,
        };
      });

    return {
      categories,
      items,
      source:
        "supabase",
    };
  } catch (error) {
    console.warn(
      "Nova live menu unavailable; using canonical fallback.",
      error,
    );

    return fallbackCatalog();
  }
}

export async function loadNovaWebsiteMenuCatalog(): Promise<NovaMenuCatalog> {
  return loadNovaMenuCatalog(
    "website_visible",
  );
}

export async function loadNovaQrMenuCatalog(): Promise<NovaMenuCatalog> {
  return loadNovaMenuCatalog(
    "qr_visible",
  );
}
